// ABOUTME: Full-screen hands-free voice conversation UI. Opens one WebSocket
// to /agent/voice/live: streams mic audio in, shows "thinking" while the
// Hermes agent answers, plays streamed spoken replies back via MediaSource,
// and lets the owner's own voice interrupt (barge-in) whatever the assistant
// is saying. The ElevenLabs key never reaches this component — only a
// short-lived ticket does.

import { useCallback, useEffect, useRef, useState } from 'react'
import { Loader2, Mic, PhoneOff } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { useLiveVoiceCapture } from '@/hooks/useLiveVoiceCapture'
import { buildLiveVoiceWsUrl, mintLiveVoiceTicket } from '@/lib/liveVoiceClient'

const MP3_MIME = 'audio/mpeg'
// Two consecutive loud frames (~200ms of the worklet's 100ms frames) before
// treating it as the owner interrupting, so a stray cough or a chair creak
// mid-reply doesn't cut off the assistant.
const BARGE_IN_RMS_THRESHOLD = 0.05
const BARGE_IN_CONSECUTIVE_FRAMES = 2

type CallState = 'connecting' | 'listening' | 'thinking' | 'speaking' | 'error' | 'ended'

interface LiveConversationOverlayProps {
  open: boolean
  onClose: () => void
  /** The team member to talk to; omit for Marketing. */
  roleId?: string
}

interface ServerEvent {
  type: string
  text?: string
  code?: string
  message?: string
  balance?: number
}

export function LiveConversationOverlay({ open, onClose, roleId }: LiveConversationOverlayProps) {
  const [callState, setCallState] = useState<CallState>('connecting')
  const [captionText, setCaptionText] = useState('')
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [turnMessage, setTurnMessage] = useState<string | null>(null)

  const wsRef = useRef<WebSocket | null>(null)
  const mediaSourceRef = useRef<MediaSource | null>(null)
  const sourceBufferRef = useRef<SourceBuffer | null>(null)
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const pendingMp3Ref = useRef<ArrayBuffer[]>([])
  const speechFrameStreakRef = useRef(0)
  // The mic-capture AudioWorklet's onmessage handler is bound once, the first
  // time the WebSocket opens, so it closes over whatever `callState` existed
  // at that moment ('connecting') and never sees later renders. A ref always
  // reads the latest value regardless of when the closure that reads it was
  // created, so barge-in detection stays correct after the call state moves
  // on to 'speaking'.
  const callStateRef = useRef<CallState>(callState)
  useEffect(() => {
    callStateRef.current = callState
  }, [callState])

  const stopPlayback = useCallback(() => {
    const audio = audioRef.current
    if (audio) {
      audio.pause()
      audio.removeAttribute('src')
      audio.load()
    }
    mediaSourceRef.current = null
    sourceBufferRef.current = null
    pendingMp3Ref.current = []
  }, [])

  const bargeSentRef = useRef(false)

  const beginReplyPlayback = useCallback(() => {
    bargeSentRef.current = false
    stopPlayback()
    const audio = new Audio()
    audioRef.current = audio
    const mediaSource = new MediaSource()
    mediaSourceRef.current = mediaSource
    audio.src = URL.createObjectURL(mediaSource)
    mediaSource.addEventListener(
      'sourceopen',
      () => {
        if (mediaSourceRef.current !== mediaSource) return
        const sourceBuffer = mediaSource.addSourceBuffer(MP3_MIME)
        sourceBufferRef.current = sourceBuffer
        sourceBuffer.addEventListener('updateend', () => {
          const next = pendingMp3Ref.current.shift()
          if (next && !sourceBuffer.updating) sourceBuffer.appendBuffer(next)
        })
        const queued = pendingMp3Ref.current
        pendingMp3Ref.current = []
        for (const chunk of queued) {
          if (!sourceBuffer.updating) sourceBuffer.appendBuffer(chunk)
          else pendingMp3Ref.current.push(chunk)
        }
      },
      { once: true }
    )
    void audio.play().catch(() => {
      /* autoplay may be blocked until a user gesture; the mic-permission
       * gesture that opened this overlay generally satisfies that. */
    })
  }, [stopPlayback])

  const appendReplyChunk = useCallback((chunk: ArrayBuffer) => {
    const sourceBuffer = sourceBufferRef.current
    if (!sourceBuffer) {
      pendingMp3Ref.current.push(chunk)
      return
    }
    if (sourceBuffer.updating || pendingMp3Ref.current.length > 0) {
      pendingMp3Ref.current.push(chunk)
    } else {
      sourceBuffer.appendBuffer(chunk)
    }
  }, [])

  // The one place that actually interrupts a spoken reply: stops local
  // playback and tells the server, so a barge-in is consistent regardless of
  // which signal detected it first — the owner's own local RMS (fast, but a
  // heuristic) or the server's STT noticing speech over the reply (slower to
  // arrive, but authoritative). Idempotent per reply: `beginReplyPlayback`
  // resets the flag, so at most one `barge_in` frame goes out per reply.
  const sendBargeIn = useCallback(() => {
    if (bargeSentRef.current) return
    bargeSentRef.current = true
    stopPlayback()
    setCaptionText('')
    const ws = wsRef.current
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: 'barge_in' }))
    }
  }, [stopPlayback])

  const handleServerEvent = useCallback(
    (event: ServerEvent) => {
      switch (event.type) {
        case 'partial_transcript':
        case 'transcript':
          // The server's own STT noticed the owner talking. While a reply is
          // playing, that IS a barge-in — without this, the reply kept
          // playing silently past this event (state moved to 'listening'
          // with nothing telling the server or stopping the audio), and the
          // owner's local RMS check no longer fired because it already saw
          // 'speaking' flip away underneath it.
          if (callStateRef.current === 'speaking') sendBargeIn()
          setCaptionText(event.text ?? '')
          setTurnMessage(null)
          setCallState('listening')
          break
        case 'thinking':
          setCallState('thinking')
          break
        case 'turn_error':
          // One turn ended without a spoken reply; the call keeps listening.
          stopPlayback()
          setTurnMessage(event.message ?? 'No reply this time.')
          setCallState('listening')
          break
        case 'reply_text':
          setCaptionText(event.text ?? '')
          setCallState('speaking')
          beginReplyPlayback()
          break
        case 'error':
          setErrorMessage(event.message ?? 'Live conversation hit a problem.')
          setCallState('error')
          break
        default:
          break
      }
    },
    [beginReplyPlayback, sendBargeIn, stopPlayback]
  )

  const handleAudioFrame = useCallback((pcm16: ArrayBuffer) => {
    const ws = wsRef.current
    if (ws && ws.readyState === WebSocket.OPEN) ws.send(pcm16)
  }, [])

  const handleLevel = useCallback(
    (rms: number) => {
      if (callStateRef.current !== 'speaking') {
        speechFrameStreakRef.current = 0
        return
      }
      if (rms >= BARGE_IN_RMS_THRESHOLD) {
        speechFrameStreakRef.current += 1
        if (speechFrameStreakRef.current >= BARGE_IN_CONSECUTIVE_FRAMES) {
          speechFrameStreakRef.current = 0
          setCallState('listening')
          sendBargeIn()
        }
      } else {
        speechFrameStreakRef.current = 0
      }
    },
    [sendBargeIn]
  )

  const capture = useLiveVoiceCapture({
    onAudioFrame: handleAudioFrame,
    onLevel: handleLevel,
    onError: (message) => {
      setErrorMessage(message)
      setCallState('error')
    },
  })

  const endCall = useCallback(() => {
    const ws = wsRef.current
    if (ws && ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify({ type: 'end' }))
      ws.close()
    }
    wsRef.current = null
    capture.stop()
    stopPlayback()
    onClose()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onClose, stopPlayback])

  useEffect(() => {
    if (!open) return
    let cancelled = false
    setCallState('connecting')
    setErrorMessage(null)
    setTurnMessage(null)
    setCaptionText('')

    void (async () => {
      try {
        const ticket = await mintLiveVoiceTicket(roleId)
        if (cancelled) return
        const ws = new WebSocket(buildLiveVoiceWsUrl(ticket))
        ws.binaryType = 'arraybuffer'
        wsRef.current = ws

        ws.addEventListener('open', () => {
          if (cancelled) return
          setCallState('listening')
          void capture.start()
        })
        ws.addEventListener('message', (event) => {
          if (typeof event.data === 'string') {
            try {
              handleServerEvent(JSON.parse(event.data) as ServerEvent)
            } catch {
              // ignore malformed frame
            }
          } else if (event.data instanceof ArrayBuffer) {
            appendReplyChunk(event.data)
          }
        })
        ws.addEventListener('close', () => {
          if (cancelled) return
          setCallState((prev) => (prev === 'error' ? prev : 'ended'))
        })
        ws.addEventListener('error', () => {
          if (cancelled) return
          setErrorMessage('Lost connection to live conversation.')
          setCallState('error')
        })
      } catch (err) {
        if (cancelled) return
        setErrorMessage(err instanceof Error ? err.message : 'Could not start live conversation.')
        setCallState('error')
      }
    })()

    return () => {
      cancelled = true
      wsRef.current?.close()
      wsRef.current = null
      capture.stop()
      stopPlayback()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, roleId])

  if (!open) return null

  return (
    // z-[60]: above the mobile bottom nav (z-50, and later in DOM order —
    // equal z-index would otherwise let it paint on top and stay tappable
    // underneath this full-screen call UI).
    <div className="bg-background fixed inset-0 z-[60] flex flex-col items-center justify-center gap-6 px-6">
      <Button
        variant="ghost"
        size="icon-sm"
        onClick={endCall}
        aria-label="Close live conversation"
        className="absolute top-4 right-4"
      >
        <PhoneOff className="size-4" />
      </Button>

      <div
        className={cn(
          'flex size-32 items-center justify-center rounded-full transition-colors',
          callState === 'speaking' && 'bg-primary/20',
          callState === 'listening' && 'bg-primary/10',
          (callState === 'connecting' || callState === 'thinking') && 'bg-muted',
          callState === 'error' && 'bg-destructive/10'
        )}
        data-testid="live-voice-orb"
        data-call-state={callState}
      >
        {callState === 'connecting' ? (
          <Loader2 className="text-muted-foreground size-10 animate-spin" />
        ) : (
          <Mic
            className={cn(
              'size-10',
              callState === 'speaking' ? 'text-primary' : 'text-muted-foreground'
            )}
          />
        )}
      </div>

      <p className="text-muted-foreground text-sm" data-testid="live-voice-status">
        {callState === 'connecting' && 'Connecting…'}
        {callState === 'listening' && 'Listening…'}
        {callState === 'thinking' && 'Thinking…'}
        {callState === 'speaking' && 'Speaking…'}
        {callState === 'ended' && 'Call ended.'}
        {callState === 'error' && (errorMessage ?? 'Something went wrong.')}
      </p>

      {turnMessage && (
        <p
          className="text-muted-foreground max-w-md text-center text-sm"
          data-testid="live-voice-turn-message"
        >
          {turnMessage}
        </p>
      )}

      {captionText && (
        <p className="max-w-md text-center text-base" data-testid="live-voice-caption">
          {captionText}
        </p>
      )}

      <Button variant="outline" onClick={endCall} data-testid="live-voice-end-call">
        End conversation
      </Button>
    </div>
  )
}

export default LiveConversationOverlay
